import { useState } from "react";
import { PermissionSet, type MyMembershipsResponse, type StaffRole } from "@starsach/shared-types";
import { Hint } from "../../components/Hint";
import { Card, DataTable, EmptyState, StatusPill, sentence } from "../../components/ui";
import { SECTIONS, label, sectionRows } from "../../lib/permissions";

const STAFF_ROLE_WORDS: Record<StaffRole, string> = {
  "gairm-staff": "every permission on every environment you are not excluded from",
  "gairm-reader": "read on every section of every environment you are not excluded from; nothing is changed, and no transcript is read",
  "gairm-superadmin": "every permission on every environment, and the setting of staff roles and exclusions on the Platform view",
};

/**
 * Section 13.4 and ADR-0017 section 6: the effective permissions per
 * environment, with which role granted each - "Edit hours and holidays on
 * Production - Holidays editor, Manager" - grouped by section. Read-only:
 * every grant still happens on the environment's Access module.
 */
export function MyAccess({ data }: { data: MyMembershipsResponse }) {
  const [chosen, setChosen] = useState<string>();
  const environments = data.access;
  const current = environments.find((a) => a.environmentId === chosen) ?? environments[0];
  if (data.scope === "internal-team") {
    return (
      <Card title="Where you have access">
        <p className="text-sm text-neutral-700 dark:text-neutral-300">
          You are on the internal team as <StatusPill status={data.staffRole ?? "gairm-staff"} tone={data.staffRole === "gairm-reader" ? "neutral" : "ok"} />
          <Hint id="access.staff-role" label="what a staff role is" />: {STAFF_ROLE_WORDS[data.staffRole ?? "gairm-staff"]}. That access comes from your group, not
          from per-environment membership, so nothing is granted here.
        </p>
        {data.staffRole === "gairm-reader" && current && <PermissionList access={current} envName="every environment" />}
        {environments.length > 0 && data.staffRole !== "gairm-reader" && (
          <p className="mt-2 text-xs text-neutral-500">
            {environments.length} environment{environments.length === 1 ? "" : "s"} visible to you.
          </p>
        )}
      </Card>
    );
  }
  return (
    <Card title="Where you have access">
      <DataTable
        caption="Environments you hold a role on, and the role you hold"
        rowKey={(m) => m.environmentId}
        sortBy={(m) => m.friendlyName ?? m.environmentId}
        rows={data.memberships}
        empty={<EmptyState title="You do not hold a role on any environment yet" hint="A manager on an environment can grant you one from its Access module." />}
        columns={[
          {
            key: "environment",
            header: "Environment",
            render: (m) => (
              <div>
                <div className="text-neutral-900 dark:text-neutral-100">{m.friendlyName ?? m.environmentId}</div>
                <div className="font-mono text-xs text-neutral-600 dark:text-neutral-400">{m.awsAccountNumber ?? m.environmentId}</div>
              </div>
            ),
          },
          {
            key: "roles",
            header: "Your roles",
            render: (m) => {
              const a = environments.find((x) => x.environmentId === m.environmentId);
              const names = [...new Set(Object.values(a?.grantedBy ?? {}).flat())];
              return names.length === 0 ? <StatusPill status={m.role} tone={m.role === "manager" ? "ok" : "neutral"} /> : <span className="flex flex-wrap gap-1">{names.map((n) => <StatusPill key={n} status={n} tone={n === "Manager" ? "ok" : "neutral"} />)}</span>;
            },
          },
          {
            key: "profiles",
            header: "Profiles you may see",
            render: (m) => {
              const a = environments.find((x) => x.environmentId === m.environmentId);
              return a?.allProfiles === false ? <StatusPill status={`${a.profileIds?.length ?? 0} chosen`} tone="warning" /> : <StatusPill status="all" tone="neutral" />;
            },
          },
          {
            key: "what",
            header: "",
            render: (m) => (
              <button type="button" className="text-sm underline" aria-pressed={current?.environmentId === m.environmentId} onClick={() => setChosen(m.environmentId)}>
                What you can do
              </button>
            ),
          },
        ]}
      />
      {current && <PermissionList access={current} envName={current.friendlyName ?? current.environmentId} />}
    </Card>
  );
}

/** Every permission in force on one environment, by section, with the roles that grant it. */
function PermissionList({ access, envName }: { access: MyMembershipsResponse["access"][number]; envName: string }) {
  const set = new PermissionSet(access.permissions, access.grantedBy);
  const enabled = SECTIONS.filter((s) => set.has(`${s}:enabled`));
  const grantedBy = (p: string) => (set.grantedBy[p] ?? []).join(", ");
  return (
    <section aria-label={`What you can do on ${envName}`} className="mt-4 border-t border-neutral-200 pt-4 dark:border-neutral-800">
      <h3 className="text-sm font-semibold">
        What you can do on {envName}
        <Hint id="access.union-rule" label="how several roles combine" />
      </h3>
      {enabled.length === 0 ? (
        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">Nothing yet: no role you hold here enables a section.</p>
      ) : (
        <ul className="mt-2 grid gap-3 md:grid-cols-2">
          {enabled.map((section) => {
            const lines = sectionRows(section).flatMap((r) => r.verbs.filter((v) => set.has(`${r.key}:${v}`)).map((v) => ({ key: `${r.key}:${v}`, text: `${label(v)} ${r.label.toLowerCase()}` })));
            return (
              <li key={section} className="rounded-lg border border-neutral-200 p-3 text-sm dark:border-neutral-800">
                <p className="font-medium">
                  {label(section)} <span className="text-xs font-normal text-neutral-500">- {grantedBy(`${section}:enabled`)}</span>
                </p>
                {lines.length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-xs text-neutral-700 dark:text-neutral-300">
                    {lines.map((l) => (
                      <li key={l.key}>
                        {sentence(l.text)} on {envName} <span className="text-neutral-500">- {grantedBy(l.key)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
